// Страница управления студентами для администраторов: таблица с поиском, принудительным завершением и удалением.

import { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  Modal,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppNavbar from '../../components/common/AppNavbar';
import {
  getAdminStudents,
  forceCompleteStudentSessions,
  deleteAdminStudent,
} from '../../api/adminApi';

/** Инициалы из имени */
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/** Склонение слова «студент» */
function pluralStudents(n) {
  if (n === 1) return '1 студент';
  if (n >= 2 && n <= 4) return `${n} студента`;
  return `${n} студентов`;
}

export default function AdminStudentsPage() {
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState(null);
  // confirmModal shape: { type: 'force-complete' | 'delete', student, actionLoading }

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    loadStudents();
  }, []);

  async function loadStudents() {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminStudents();
      setStudents(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail ?? 'Не удалось загрузить список студентов');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.trim().toLowerCase();
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q)
    );
  }, [students, search]);

  const openForceComplete = (student) => {
    setActionError('');
    setConfirmModal({ type: 'force-complete', student });
  };

  const openDelete = (student) => {
    setActionError('');
    setConfirmModal({ type: 'delete', student });
  };

  const closeModal = () => {
    if (actionLoading) return;
    setConfirmModal(null);
    setActionError('');
  };

  const handleConfirm = async () => {
    if (!confirmModal) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (confirmModal.type === 'force-complete') {
        await forceCompleteStudentSessions(confirmModal.student.student_id);
        // Refresh just this student row by reloading the full list
        const res = await getAdminStudents();
        setStudents(res.data);
      } else if (confirmModal.type === 'delete') {
        await deleteAdminStudent(confirmModal.student.student_id);
        setStudents((prev) => prev.filter((s) => s.student_id !== confirmModal.student.student_id));
      }
      setConfirmModal(null);
    } catch (err) {
      setActionError(err?.response?.data?.detail ?? 'Произошла ошибка. Попробуйте ещё раз.');
    } finally {
      setActionLoading(false);
    }
  };

  const isForceComplete = confirmModal?.type === 'force-complete';

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── Заголовок ── */}
          <div
            className="d-flex justify-content-between align-items-center mb-4 pb-3"
            style={{ borderBottom: '1px solid #e9ecef' }}
          >
            <div>
              <h5 className="fw-bold mb-0" style={{ color: '#1e2a4a', letterSpacing: '-0.01em' }}>
                Студенты
              </h5>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                {loading
                  ? 'Загрузка...'
                  : students.length > 0
                  ? pluralStudents(students.length)
                  : 'Нет студентов'}
              </p>
            </div>

            {/* Поиск */}
            {!loading && students.length > 0 && (
              <Form.Control
                type="search"
                placeholder="Поиск по имени или email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  maxWidth: '280px',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                }}
              />
            )}
          </div>

          {/* ── Состояния ── */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                Загрузка студентов...
              </p>
            </div>
          ) : error ? (
            <Alert variant="danger" style={{ borderRadius: '0.875rem' }}>
              {error}
            </Alert>
          ) : students.length === 0 ? (
            <div
              className="text-center py-5"
              style={{
                background: '#fff',
                borderRadius: '0.875rem',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎓</div>
              <h6 className="fw-semibold text-muted mb-1">Студентов нет</h6>
              <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                Зарегистрированные студенты появятся здесь
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="text-center py-5"
              style={{
                background: '#fff',
                borderRadius: '0.875rem',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
              <h6 className="fw-semibold text-muted mb-1">Ничего не найдено</h6>
              <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                Попробуйте изменить поисковый запрос
              </p>
            </div>
          ) : (

            /* ── Таблица ── */
            <div className="table-wrapper">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '3rem' }}>#</th>
                    <th>Студент</th>
                    <th>Email</th>
                    <th style={{ width: '8rem' }}>Статус</th>
                    <th style={{ width: '7rem' }}>Попыток</th>
                    <th style={{ width: '7rem' }}>Завершено</th>
                    <th style={{ width: '7rem' }}>Незавершено</th>
                    <th style={{ width: '18rem' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((student, idx) => (
                    <tr
                      key={student.student_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/students/${student.student_id}`)}
                    >
                      <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {idx + 1}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="ewl-avatar"
                            style={{ opacity: student.is_active ? 1 : 0.45 }}
                          >
                            {getInitials(student.name)}
                          </div>
                          <span
                            className="fw-medium"
                            style={{ color: student.is_active ? '#1e2a4a' : '#adb5bd' }}
                          >
                            {student.name}
                          </span>
                        </div>
                      </td>
                      <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {student.email}
                      </td>
                      <td>
                        <Badge
                          bg={student.is_active ? 'success' : 'secondary'}
                          style={{ fontSize: '0.7rem', fontWeight: 600 }}
                        >
                          {student.is_active ? 'Активен' : 'Неактивен'}
                        </Badge>
                      </td>
                      <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {student.total_attempts ?? 0}
                      </td>
                      <td>
                        {(student.completed_attempts ?? 0) > 0 ? (
                          <Badge bg="success" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                            {student.completed_attempts}
                          </Badge>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '0.875rem' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(student.pending_attempts ?? 0) > 0 ? (
                          <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                            {student.pending_attempts}
                          </Badge>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '0.875rem' }}>0</span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => navigate(`/admin/students/${student.student_id}`)}
                          style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8rem' }}
                        >
                          Подробнее
                        </Button>
                        {(student.pending_attempts ?? 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="me-2"
                            onClick={() => openForceComplete(student)}
                            style={{ borderRadius: '0.4rem', fontSize: '0.8rem' }}
                          >
                            Завершить сессии
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => openDelete(student)}
                          style={{ borderRadius: '0.4rem', fontSize: '0.8rem' }}
                        >
                          Удалить
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Container>
      </div>

      {/* ── Модальное окно подтверждения ── */}
      <Modal show={Boolean(confirmModal)} onHide={closeModal}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {isForceComplete ? 'Завершить незавершённые сессии' : 'Удалить студента'}
            </Modal.Title>
            <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
              {confirmModal?.student?.name}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {actionError && (
            <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              {actionError}
            </Alert>
          )}

          {isForceComplete ? (
            <p className="mb-0" style={{ fontSize: '0.9375rem' }}>
              Будут принудительно завершены{' '}
              <strong>
                {confirmModal?.student?.pending_attempts ?? 0}{' '}
                {confirmModal?.student?.pending_attempts === 1
                  ? 'незавершённая сессия'
                  : 'незавершённых сессий'}
              </strong>{' '}
              студента <strong>{confirmModal?.student?.name}</strong>.
              Это действие нельзя отменить.
            </p>
          ) : (
            <p className="mb-0" style={{ fontSize: '0.9375rem' }}>
              Аккаунт студента <strong>{confirmModal?.student?.name}</strong> и все связанные
              данные (
              <strong>{confirmModal?.student?.total_attempts ?? 0}</strong> попыток) будут
              удалены безвозвратно.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={actionLoading}>
            Отмена
          </Button>
          <Button
            variant={isForceComplete ? 'warning' : 'danger'}
            onClick={handleConfirm}
            disabled={actionLoading}
            style={{ fontWeight: 500 }}
          >
            {actionLoading ? (
              <Spinner size="sm" animation="border" />
            ) : isForceComplete ? (
              'Завершить'
            ) : (
              'Удалить'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
